/**
 * VisibilityFieldset - Reusable Visibility card for any record that
 * scopes access by "visible to all" + per-company + per-user pivots.
 *
 * Owns:
 *   - The "Visible to all users" toggle at the top.
 *   - Two side-by-side checkbox lists (Companies | Users) with
 *     per-list filter inputs and counts.
 *   - Defensive ID-based dedupe on both options arrays so the same
 *     company/user can never render twice — the /companies endpoint
 *     has been observed to repeat rows when scoped by company_users
 *     joins, and React rendering the same key twice causes a subtle
 *     bug where toggling one checkbox visually toggles every duplicate.
 *
 * Does NOT own:
 *   - Fetching companies/users (parent supplies them).
 *   - Persisting the selection (parent reads back via onChange* props).
 *
 * Why a molecule rather than a hook + raw JSX: the visual treatment
 * (card chrome, two-column grid, disabled-dim when "visible to all"
 * is on, filter input dimensions) is intentionally identical across
 * every record type that exposes this section, so packaging the
 * markup ensures all callers stay visually aligned.
 */
import React, { useMemo, useState } from 'react';
import { Checkbox } from '../../atoms';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Generic option shape used for both company and user rows. The
 * `secondary` slot renders next to the primary `name` in muted
 * styling — used today for the user's email; companies leave it
 * undefined.
 */
export interface VisibilityOption {
    id: number;
    name: string;
    /** Optional muted-secondary text rendered next to `name`. */
    secondary?: string;
}

export interface VisibilityFieldsetProps {
    /** Card title — defaults to "Visibility". */
    title?: string;
    /** Optional muted help text shown next to the "Visible to all" toggle. */
    helpText?: string;
    /** Companies offered as visibility targets. Deduped by id internally. */
    companies: VisibilityOption[];
    /** Users offered as visibility targets. Deduped by id internally. */
    users: VisibilityOption[];
    /** When true, both lists dim/disable and the pivot selections are ignored on save. */
    isVisibleToAll: boolean;
    onChangeIsVisibleToAll: (next: boolean) => void;
    /** Selected company ids. */
    selectedCompanyIds: Set<number>;
    onChangeSelectedCompanyIds: (next: Set<number>) => void;
    /** Selected user ids. */
    selectedUserIds: Set<number>;
    onChangeSelectedUserIds: (next: Set<number>) => void;
    /** Optional className for the outer card (rarely needed). */
    className?: string;
}

/* ============================================
 * DEDUPE HELPER
 * ============================================
 *
 * Defensive pass that drops repeated rows while preserving the first-
 * seen order. Dedupes on BOTH id AND a case-insensitive name+secondary
 * signature: a previous dev-DB state had multiple Company rows with the
 * same name but distinct company_ids (seeder re-runs), and id-only
 * dedupe let every copy render. Name-level dedupe is safe here because
 * this list is for visibility scoping — two records that look identical
 * to the editor would behave identically as visibility targets. */
function dedupeById<T extends { id: number; name: string; secondary?: string }>(rows: T[]): T[] {
    const seenIds = new Set<number>();
    const seenLabels = new Set<string>();
    const out: T[] = [];
    for (const row of rows) {
        const id = Number(row.id);
        if (!Number.isFinite(id) || seenIds.has(id)) continue;
        const label = `${row.name ?? ''}${row.secondary ?? ''}`.toLowerCase();
        if (seenLabels.has(label)) continue;
        seenIds.add(id);
        seenLabels.add(label);
        out.push(row);
    }
    return out;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const VisibilityFieldset: React.FC<VisibilityFieldsetProps> = ({
    title = 'Visibility',
    helpText = '— overrides the company/user pivots below',
    companies,
    users,
    isVisibleToAll,
    onChangeIsVisibleToAll,
    selectedCompanyIds,
    onChangeSelectedCompanyIds,
    selectedUserIds,
    onChangeSelectedUserIds,
    className,
}) => {
    /* Local filter strings — kept inside the molecule because they
     * have no value outside it. Parents only care about the selected
     * ids, not which filter text the user typed. */
    const [companySearch, setCompanySearch] = useState('');
    const [userSearch, setUserSearch] = useState('');

    /* Dedupe defensively at the molecule boundary so every caller
     * gets this fix for free — including any that forget to dedupe
     * upstream. Cheap to run; the lists are small. */
    const uniqueCompanies = useMemo(() => dedupeById(companies), [companies]);
    const uniqueUsers = useMemo(() => dedupeById(users), [users]);

    /* Apply the filter on top of the deduped list. Match against
     * name + secondary so e.g. filtering users by email works. */
    const filteredCompanies = useMemo(() => {
        const q = companySearch.trim().toLowerCase();
        if (!q) return uniqueCompanies;
        return uniqueCompanies.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                (c.secondary ?? '').toLowerCase().includes(q)
        );
    }, [uniqueCompanies, companySearch]);

    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        if (!q) return uniqueUsers;
        return uniqueUsers.filter(
            (u) =>
                u.name.toLowerCase().includes(q) ||
                (u.secondary ?? '').toLowerCase().includes(q)
        );
    }, [uniqueUsers, userSearch]);

    /* Toggle helpers — clone the Set so React detects the change
     * and re-renders. Passing back a fresh Set keeps the API shape
     * symmetric to a useState setter. */
    const toggleCompany = (id: number) => {
        const next = new Set(selectedCompanyIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onChangeSelectedCompanyIds(next);
    };
    const toggleUser = (id: number) => {
        const next = new Set(selectedUserIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onChangeSelectedUserIds(next);
    };

    return (
        <section
            className={[
                'visibility-fieldset',
                'bg-white border border-brand-border rounded-md p-16px tablet:p-20px',
                className ?? '',
            ].join(' ')}
            data-ref="molecule-visibility-fieldset"
        >
            <h2 className="visibility-fieldset__header text-16px font-semibold text-brand-text mb-12px">{title}</h2>

            {/* "Visible to all" toggle — disables the multi-selects while on */}
            <Checkbox
                checked={isVisibleToAll}
                onChange={onChangeIsVisibleToAll}
                className="visibility-fieldset__toggle items-center mb-16px"
                dataRef="molecule-visibility-fieldset-toggle"
                label={(
                    <>
                        Visible to all users
                        {helpText && <span className="text-12px text-brand-text-muted ml-8px">{helpText}</span>}
                    </>
                )}
            />

            {/* Two-column: companies | users. The grid collapses to a
             * single column under the tablet breakpoint to keep the
             * lists readable on phones. */}
            <div
                className={`visibility-fieldset__grid grid grid-cols-1 tablet:grid-cols-2 gap-16px ${
                    isVisibleToAll ? 'visibility-fieldset__grid--disabled opacity-50 pointer-events-none' : ''
                }`}
            >
                {/* Companies list */}
                <div className="visibility-fieldset__list">
                    <div className="flex items-center justify-between mb-8px">
                        <label className="text-12px font-medium text-brand-text">
                            Companies ({selectedCompanyIds.size})
                        </label>
                        <input
                            type="text"
                            value={companySearch}
                            onChange={(e) => setCompanySearch(e.target.value)}
                            placeholder="Filter..."
                            className="w-[140px] px-8px py-4px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-primary"
                        />
                    </div>
                    <div className="max-h-[260px] overflow-y-auto border border-brand-border rounded-md p-8px bg-white">
                        {filteredCompanies.length === 0 && (
                            <div className="text-12px text-brand-text-muted px-4px py-2px">
                                No matches
                            </div>
                        )}
                        {filteredCompanies.map((c) => (
                            <Checkbox
                                key={c.id}
                                checked={selectedCompanyIds.has(c.id)}
                                onChange={() => toggleCompany(c.id)}
                                className="visibility-fieldset__option w-full items-center px-4px rounded-sm hover:bg-brand-surface"
                                labelClassName="text-13px"
                                dataRef="molecule-visibility-fieldset-option"
                                label={(
                                    <>
                                        {c.name}
                                        {c.secondary && (
                                            <span className="text-12px text-brand-text-muted ml-4px">{c.secondary}</span>
                                        )}
                                    </>
                                )}
                            />
                        ))}
                    </div>
                </div>

                {/* Users list */}
                <div className="visibility-fieldset__list">
                    <div className="flex items-center justify-between mb-8px">
                        <label className="text-12px font-medium text-brand-text">
                            Users ({selectedUserIds.size})
                        </label>
                        <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder="Filter..."
                            className="w-[140px] px-8px py-4px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-primary"
                        />
                    </div>
                    <div className="max-h-[260px] overflow-y-auto border border-brand-border rounded-md p-8px bg-white">
                        {filteredUsers.length === 0 && (
                            <div className="text-12px text-brand-text-muted px-4px py-2px">
                                No matches
                            </div>
                        )}
                        {filteredUsers.map((u) => (
                            <Checkbox
                                key={u.id}
                                checked={selectedUserIds.has(u.id)}
                                onChange={() => toggleUser(u.id)}
                                className="visibility-fieldset__option w-full items-center px-4px rounded-sm hover:bg-brand-surface"
                                labelClassName="text-13px"
                                dataRef="molecule-visibility-fieldset-option"
                                label={(
                                    <>
                                        {u.name}
                                        {u.secondary && (
                                            <span className="text-12px text-brand-text-muted ml-4px">{u.secondary}</span>
                                        )}
                                    </>
                                )}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default VisibilityFieldset;
